// Color utility functions for ColorWheelIcon component

// Optimized color theory with caching and LAB color space
export const colorCache = new Map<string, string>();

// Calculate contrast ratio between two colors for readability
export const calculateContrastRatio = (color1: string, color2: string): number => {
    const getLuminance = (color: string): number => {
        const hex = color.replace('#', '');
        const r = parseInt(hex.slice(0, 2), 16) / 255;
        const g = parseInt(hex.slice(2, 4), 16) / 255;
        const b = parseInt(hex.slice(4, 6), 16) / 255;

        const toLinear = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        const lr = toLinear(r) * 0.2126;
        const lg = toLinear(g) * 0.7152;
        const lb = toLinear(b) * 0.0722;

        return lr + lg + lb;
    };

    const lum1 = getLuminance(color1);
    const lum2 = getLuminance(color2);
    const lighter = Math.max(lum1, lum2);
    const darker = Math.min(lum1, lum2);

    return (lighter + 0.05) / (darker + 0.05);
};

// Choose best text color (white or black) based on contrast with background colors
export const chooseBestTextColor = (bgColor: string, uiColor: string, navColor: string): string => {
    const white = '#ffffff';
    const black = '#000000';

    // Calculate contrast ratios against all theme colors
    const bgWhiteContrast = calculateContrastRatio(bgColor, white);
    const bgBlackContrast = calculateContrastRatio(bgColor, black);
    const uiWhiteContrast = calculateContrastRatio(uiColor, white);
    const uiBlackContrast = calculateContrastRatio(uiColor, black);
    const navWhiteContrast = calculateContrastRatio(navColor, white);
    const navBlackContrast = calculateContrastRatio(navColor, black);

    // Average contrast across all theme colors
    const avgWhiteContrast = (bgWhiteContrast + uiWhiteContrast + navWhiteContrast) / 3;
    const avgBlackContrast = (bgBlackContrast + uiBlackContrast + navBlackContrast) / 3;

    // Return the color with better average contrast (WCAG AA requires 4.5:1, but we'll use 7:1 for better readability)
    return avgWhiteContrast > avgBlackContrast ? white : black;
};

// Fast HSL to RGB conversion with caching
export const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
    const cacheKey = `hsl_${h.toFixed(1)}_${s.toFixed(1)}_${l.toFixed(1)}`;
    if (colorCache.has(cacheKey)) {
        const hex = colorCache.get(cacheKey)!;
        return [
            parseInt(hex.slice(1, 3), 16),
            parseInt(hex.slice(3, 5), 16),
            parseInt(hex.slice(5, 7), 16)
        ];
    }

    const hue = h / 360;
    const sat = s / 100;
    const light = l / 100;

    const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };

    let r, g, b;
    if (sat === 0) {
        r = g = b = light;
    } else {
        const q = light < 0.5 ? light * (1 + sat) : light + sat - light * sat;
        const p = 2 * light - q;
        r = hue2rgb(p, q, hue + 1 / 3);
        g = hue2rgb(p, q, hue);
        b = hue2rgb(p, q, hue - 1 / 3);
    }

    return [r, g, b];
};

// Convert HSL to hex with caching
export const hslToHex = (h: number, s: number, l: number): string => {
    const cacheKey = `hsl2hex_${h.toFixed(1)}_${s.toFixed(1)}_${l.toFixed(1)}`;

    if (colorCache.has(cacheKey)) {
        return colorCache.get(cacheKey)!;
    }

    const [r, g, b] = hslToRgb(h, s, l);
    const toHex = (c: number) => {
        const hex = Math.round(c * 255).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };

    const hex = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    colorCache.set(cacheKey, hex);
    return hex;
};

// Generate background colors based on nav color
export const generateBackgroundFromNav = (navHex: string) => {
    // Parse the nav color
    const r = parseInt(navHex.slice(1, 3), 16);
    const g = parseInt(navHex.slice(3, 5), 16);
    const b = parseInt(navHex.slice(5, 7), 16);

    // Convert to HSL
    const rNorm = r / 255, gNorm = g / 255, bNorm = b / 255;
    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    let h = 0, s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case rNorm: h = (gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0); break;
            case gNorm: h = (bNorm - rNorm) / d + 2; break;
            case bNorm: h = (rNorm - gNorm) / d + 4; break;
        }
        h /= 6;
    }

    const hue = h * 360;
    const saturation = s * 100;
    const lightness = l * 100;

    // Create variations for effects and canvas background
    // Effects: slightly higher saturation, similar lightness
    const effectsHue = (hue + Math.random() * 20 - 10) % 360; // Small hue variation
    const effectsSat = Math.min(100, saturation + 10 + Math.random() * 20); // Higher saturation
    const effectsLight = Math.max(20, Math.min(90, lightness + 10 + Math.random() * 20)); // Brighter

    // Canvas background: lower saturation, adjusted lightness
    const canvasHue = (hue + Math.random() * 30 - 15) % 360; // Moderate hue variation
    const canvasSat = Math.max(10, saturation - 20 + Math.random() * 30); // Lower saturation
    const canvasLight = Math.max(5, Math.min(85, lightness - 10 + Math.random() * 20)); // Varied lightness

    return {
        effectsColor: hslToHex(effectsHue, effectsSat, effectsLight),
        canvasBgColor: hslToHex(canvasHue, canvasSat, canvasLight)
    };
};

// Generate aesthetically pleasing color harmonies using LAB space
export const generateHarmoniousPalette = () => {
    // Use golden ratio for aesthetically pleasing angles
    const goldenAngle = 137.507764; // degrees
    const baseHue = Math.random() * 360;

    // Choose harmony type with weighted probabilities
    const rand = Math.random();
    let harmonyType: 'analogous' | 'complementary' | 'triadic' | 'split-complementary' | 'tetradic';

    if (rand < 0.35) harmonyType = 'analogous';
    else if (rand < 0.60) harmonyType = 'complementary';
    else if (rand < 0.80) harmonyType = 'triadic';
    else if (rand < 0.90) harmonyType = 'split-complementary';
    else harmonyType = 'tetradic';

    switch (harmonyType) {
        case 'analogous': {
            // Colors adjacent on color wheel (harmonious)
            const analogousSpread = 30 + Math.random() * 40; // 30-70 degrees
            return {
                navHue: baseHue,
                uiHue: (baseHue + analogousSpread * (Math.random() - 0.5)) % 360,
                backgroundHue: (baseHue + analogousSpread * 0.4) % 360
            };
        }

        case 'complementary':
            // Opposite colors for maximum contrast
            return {
                navHue: baseHue,
                uiHue: (baseHue + 180) % 360,
                backgroundHue: (baseHue + 180 + goldenAngle * 0.1) % 360
            };

        case 'triadic':
            // Three colors equally spaced
            return {
                navHue: baseHue,
                uiHue: (baseHue + 120) % 360,
                backgroundHue: (baseHue + 240) % 360
            };

        case 'split-complementary': {
            // Base + two colors adjacent to complement
            return {
                navHue: baseHue,
                uiHue: (baseHue + 150 + Math.random() * 60) % 360,
                backgroundHue: (baseHue + 210 + Math.random() * 60) % 360
            };
        }

        case 'tetradic':
            // Four colors in two complementary pairs
            return {
                navHue: baseHue,
                uiHue: (baseHue + 90) % 360,
                backgroundHue: (baseHue + 180) % 360
            };
    }
};

// Helper function to convert hex to RGB
export const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
};

// Helper function to convert RGB to hex
export const rgbToHex = (r: number, g: number, b: number): string => {
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};